<template>
  <!-- Where the MCP page's connector fits: the AI apps the MCP server can name (Workspace369
       lib/model/mcp/mcp_known_app.dart, mirroring functions-mcp/src/oauth/knownApps.ts: Claude,
       ChatGPT, Grok, Cursor, Windsurf; any other app that supports MCP over OAuth can connect too),
       and, separately, the automation tools that use webhooks (every plan) and the Builder API (also
       every plan) instead. Each app is a tile (logo, name, how it connects); the tiles rise once, lane
       by lane, when the block scrolls into view. -->
  <div
    ref="rootEl"
    class="mal fm-tokens"
    :class="{ 'is-run': running || armed, 'is-armed': armed }"
    style="--cycle: 2400ms"
  >
    <div class="mal-lane">
      <p class="mal-label fm-in" style="--at: 0">AI apps that connect over MCP, on every plan once an admin turns on AI connections</p>
      <ul class="mal-chips">
        <li v-for="(app, i) in aiApps" :key="app.name" class="mal-chip fm-rise" :style="{ '--at': 0.06 + i * 0.06 }">
          <img v-if="app.logo" :src="app.logo" :alt="`${app.name} logo`" width="24" height="24" :class="{ 'mal-mono': app.mono }"  loading="lazy" decoding="async" />
          <i v-else class="mal-any" aria-hidden="true">+</i>
          <span><b :translate="app.logo ? 'no' : null">{{ app.name }}</b><small>{{ app.logo ? "Connector, over OAuth" : "Any app with MCP over OAuth" }}</small></span>
        </li>
      </ul>
    </div>
    <div class="mal-lane">
      <p class="mal-label fm-in" style="--at: 0.42">Automation tools that use webhooks and the Builder API on every plan</p>
      <ul class="mal-chips">
        <li v-for="(tool, i) in automationTools" :key="tool.name" class="fm-rise" :style="{ '--at': 0.48 + i * 0.06 }">
          <NuxtLink class="mal-chip mal-link" :to="localePath(tool.path)">
            <img :src="tool.logo" :alt="`${tool.name} logo`" width="24" height="24"  loading="lazy" decoding="async" />
            <span><b translate="no">{{ tool.name }}</b><small>Webhooks and Builder API</small></span>
          </NuxtLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
const { localePath } = useWorkspaceLocale();
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.5 });

const aiApps = [
  { name: "Claude", logo: "/images/ai-connections/ai_logo_claude.svg" },
  { name: "ChatGPT", logo: "/images/ai-connections/ai_logo_chatgpt.svg", mono: true },
  { name: "Grok", logo: "/images/ai-connections/ai_logo_grok.svg", mono: true },
  { name: "Cursor", logo: "/images/ai-connections/ai_logo_cursor.svg", mono: true },
  { name: "Windsurf", logo: "/images/ai-connections/ai_logo_windsurf.svg", mono: true },
  { name: "Other MCP apps" },
];
const automationTools = [
  { name: "Zapier", logo: "/images/integration-zapier.svg", path: "/integrations/zapier" },
  { name: "Make", logo: "/images/integration-make.svg", path: "/integrations/make" },
  { name: "n8n", logo: "/images/integration-n8n.svg", path: "/integrations/n8n" },
];
</script>

<style>
.mal { display: grid; gap: 22px; margin-top: 22px; }
@media (min-width: 900px) {
  .mal { grid-template-columns: 3fr 2fr; gap: 28px; }
}
.mal-label { margin: 0 0 10px; color: var(--fm-muted); font-size: 0.8rem; line-height: 1.45; }
.mal-chips { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin: 0; padding: 0; list-style: none; }
.mal-lane:first-child .mal-chips { grid-template-columns: repeat(auto-fill, minmax(10.5rem, 1fr)); }
.mal-lane:last-child .mal-chips { grid-template-columns: minmax(0, 1fr); }
.mal-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  border: 1px solid var(--fm-line);
  border-radius: 8px;
  background: var(--fm-surface);
  padding: 10px 12px;
  color: var(--fm-text);
}
.mal-chip > span { display: grid; gap: 1px; min-width: 0; }
.mal-chip b { font-size: 0.9rem; font-weight: 600; line-height: 1.3; }
.mal-chip small { color: var(--fm-muted); font-size: 0.74rem; line-height: 1.35; }
.mal-chip img { width: 24px; height: 24px; flex: none; }
.mal-any { display: grid; place-items: center; width: 24px; height: 24px; flex: none; border: 1px dashed var(--fm-line); border-radius: 6px; color: var(--fm-muted); font-style: normal; font-weight: 600; }
/* The monochrome marks paint in currentColor, which an <img> can't inherit: flip them in dark. */
.dark .mal-mono { filter: invert(1); }
.mal-link { text-decoration: none; transition: border-color 0.2s ease; }
.mal-link:hover { border-color: var(--fm-tone); }
.mal-link:focus-visible { outline: 2px solid var(--fm-tone); outline-offset: 2px; }
</style>
